import React, { useEffect, useRef, useState } from 'react';

/**
 * LazyImage component with Intersection Observer API
 * Loads images only when they are in viewport or about to enter
 *
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for accessibility
 * @param {string} className - CSS class name
 * @param {string} placeholderColor - Background color while loading (default: #f0f0f0)
 * @param {number} rootMargin - Margin in pixels to start loading before entering viewport (default: 200)
 * @param {function} onLoad - Callback when image loads
 * @param {function} onError - Callback when image fails to load
 * @param {object} style - Additional inline styles
 */
const LazyImage = ({
  src,
  alt = '',
  className = '',
  placeholderColor = '#f0f0f0',
  rootMargin = 200,
  onLoad,
  onError,
  style = {},
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!src || hasError) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: `${rootMargin}px`,
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [src, rootMargin, hasError]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    if (onLoad) {
      onLoad(e);
    }
  };

  const handleError = (e) => {
    setHasError(true);
    setIsLoaded(false);
    if (onError) {
      onError(e);
    }
  };

  return (
    <div
      ref={imgRef}
      className={`lazy-image-container ${className}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: hasError ? 'transparent' : placeholderColor,
        ...style
      }}
    >
      {isInView && src && !hasError && (
        <img
          src={src}
          alt={alt}
          className={className}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            ...style
          }}
          {...props}
        />
      )}
      {!isLoaded && !hasError && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#999',
            fontSize: '12px'
          }}
        >
          {isInView ? 'Загрузка...' : ''}
        </div>
      )}
    </div>
  );
};

export default LazyImage;
